import React, { useState } from 'react';

/**
 * Card di un grafico con gestione degli stati:
 * - primo caricamento: skeleton della stessa altezza del grafico (niente salti di layout)
 * - ricaricamento: il grafico precedente resta visibile attenuato
 * - errore: messaggio con "Riprova"
 * - nessun dato: messaggio
 * Ogni grafico ha anche la vista tabella con gli stessi valori.
 *
 * @param {Object} request stato di useRequest ({ loading, error, reload })
 * @param {Boolean} hasData true se ci sono dati da mostrare (anche vecchi)
 * @param {Boolean} isEmpty true se i dati ci sono ma sono tutti a zero
 * @param {Function} renderTable funzione che restituisce la tabella
 */
export function ChartCard({ title, subtitle, actions, request, hasData, isEmpty, renderTable, minHeight = 240, children }) {
    const [showTable, setShowTable] = useState(false);
    const { loading, error, reload } = request;

    let body;
    if (error && !hasData) {
        body = (
            <div className="stState">
                <p>Impossibile caricare i dati.</p>
                <button type="button" className="btn btn-sm btn-outline-primary" onClick={reload}>Riprova</button>
            </div>
        );
    } else if (!hasData) {
        body = <div className="stSkeleton" style={{ height: minHeight }} />;
    } else if (isEmpty) {
        body = <div className="stState"><p>Nessun dato per il periodo selezionato.</p></div>;
    } else {
        body = showTable && renderTable ? renderTable() : children;
    }

    return (
        <section className="stCard">
            <header className="stCardHeader">
                <div className="stCardTitle">
                    <h3>{title}</h3>
                    {subtitle && <p>{subtitle}</p>}
                </div>

                <div className="stCardActions">
                    {actions}
                    {
                        renderTable && hasData && !isEmpty &&
                        <button type="button" className="stLinkButton" onClick={() => setShowTable(!showTable)}>
                            {showTable ? 'Grafico' : 'Tabella'}
                        </button>
                    }
                </div>
            </header>

            <div className={`stCardBody${loading && hasData ? ' isBusy' : ''}`} style={hasData && !isEmpty ? undefined : { minHeight }} aria-busy={loading}>
                {body}

                {
                    error && hasData &&
                    <p className="stInlineError">
                        Aggiornamento non riuscito. <button type="button" className="stLinkButton" onClick={reload}>Riprova</button>
                    </p>
                }
            </div>
        </section>
    );
}

export function DataTable({ columns, rows }) {
    return (
        <div className="stTableWrapper">
            <table className="stTable">
                <thead>
                    <tr>{columns.map(col => <th key={col.key} className={col.numeric ? 'isNumeric' : ''}>{col.label}</th>)}</tr>
                </thead>
                <tbody>
                    {
                        rows.map((row, i) => (
                            <tr key={row.key || i}>
                                {columns.map(col => <td key={col.key} className={col.numeric ? 'isNumeric' : ''}>{col.format ? col.format(row[col.key]) : row[col.key]}</td>)}
                            </tr>
                        ))
                    }
                </tbody>
            </table>
        </div>
    );
}

/**
 * Tile per un singolo numero (KPI).
 */
export function Kpi({ label, value, hint, loading, error }) {
    return (
        <div className="stKpi">
            <span className="stKpiLabel">{label}</span>
            {
                loading && value === undefined ? <span className="stSkeleton stKpiSkeleton" /> :
                    error && value === undefined ? <span className="stKpiValue isMuted">—</span> :
                        <span className={`stKpiValue${loading ? ' isBusy' : ''}`}>{value}</span>
            }
            {hint && value !== undefined && <span className="stKpiHint">{hint}</span>}
        </div>
    );
}

/**
 * Pulsanti a scelta singola (es. Lordi / Netti, VPN Pass / VPN Box).
 */
export function Segmented({ options, value, onChange, label }) {
    return (
        <div className="stSegmented" role="group" aria-label={label}>
            {
                options.map(option => (
                    <button
                        key={option.value}
                        type="button"
                        className={option.value === value ? 'isActive' : ''}
                        aria-pressed={option.value === value}
                        onClick={() => onChange(option.value)}
                    >
                        {option.label}
                    </button>
                ))
            }
        </div>
    );
}
